import { QuoteStatus } from '@pelicargo/types';
import { useCallback, useEffect, useState } from 'react';

import { trpc } from '../../config/trpc';
import { API } from '../../utils/apiTypes';
import { getBestQuoteVariants } from '../../utils/getTotalCost';
import { calculateTotalCostWithoutMarkup } from '../useQuoteVariantHelper';

type QuoteWithTotalCost = API.QuoteWithTotalCost;
type QuoteStatusType = API.QuoteWithTotalCost['status'];

const isQuoteWithTotalCost = (quote: QuoteWithTotalCost | null | undefined): quote is QuoteWithTotalCost => {
  return !!quote;
};

export const cheapestQuoteSort = (quotes: QuoteWithTotalCost[]) => {
  return quotes.toSorted((q1, q2) => {
    if (!q1.quote_variants?.length && !q2.quote_variants?.length) {
      return 0;
    } else if (!q1.quote_variants?.length) {
      return 1;
    } else if (!q2.quote_variants?.length) {
      return -1;
    }
    const bestq1 = getBestQuoteVariants(q1.quote_variants);
    const bestq2 = getBestQuoteVariants(q2.quote_variants);
    const q1rate = calculateTotalCostWithoutMarkup(bestq1[0]);
    const q2rate = calculateTotalCostWithoutMarkup(bestq2[0]);

    return q1rate.totalCostWithoutMarkup - q2rate.totalCostWithoutMarkup;
  });
};

const processAndSortQuotes = (quotes: QuoteWithTotalCost[]) => {
  const safeQuotes = quotes.filter(isQuoteWithTotalCost);
  if (!safeQuotes.length) return [];

  const priorityTypes: QuoteStatusType[] = [
    QuoteStatus.Confirmed,
    QuoteStatus.BookingRequested,
    QuoteStatus.BookingPending,
    QuoteStatus.InProgress,
  ];
  const sortedQuotes = cheapestQuoteSort(safeQuotes.filter((quote) => priorityTypes.includes(quote.status)));

  if (sortedQuotes.length) sortedQuotes[0].is_cheapest = true;
  safeQuotes.forEach((quote) => {
    if (quote.id !== sortedQuotes[0]?.id) quote.is_cheapest = false;
  });

  const expiredOrPendingQuotes = safeQuotes.filter((quote) => !priorityTypes.includes(quote.status));
  return [...sortedQuotes, ...expiredOrPendingQuotes];
};

export const useQuotes = (requestId: number) => {
  const { data, isLoading, error } = trpc.getRequestQuote.useQuery({ id: requestId }, { refetchOnWindowFocus: false });
  const [quotes, setQuotes] = useState<QuoteWithTotalCost[]>([]);

  useEffect(() => {
    const processedQuotes = processAndSortQuotes(
      data?.quotes?.length ? data?.quotes.filter(isQuoteWithTotalCost) : quotes,
    );
    setQuotes(processedQuotes);

    // don't include the quotes because it will loop infinitely...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.quotes]);

  // Update quotes when a new quote is created or updated
  const updateQuotes = useCallback(
    (newQuote: QuoteWithTotalCost, { isNew = false, id }: { isNew?: boolean; id?: number }) => {
      setQuotes((currentQuotes) => {
        const safeCurrentQuotes = currentQuotes.filter(isQuoteWithTotalCost);
        if (!newQuote && id === undefined) return processAndSortQuotes(safeCurrentQuotes);

        const updatedQuotes = isNew
          ? [...safeCurrentQuotes, newQuote]
          : id !== undefined
            ? safeCurrentQuotes.filter((quote) => quote.id !== id)
            : safeCurrentQuotes.map((quote) => (quote.id === newQuote.id ? newQuote : quote));
        return processAndSortQuotes(updatedQuotes);
      });
    },
    [setQuotes],
  );

  trpc.onQuoteCreated.useSubscription(
    { requestId },
    {
      onData: (newData: any) => {
        if (requestId) updateQuotes(newData, { isNew: true });
      },
    },
  );

  trpc.onQuoteUpdated.useSubscription(
    { requestId },
    {
      onData: (newData: any) => {
        if (requestId) updateQuotes(newData, {});
      },
    },
  );

  trpc.onQuoteDeleted.useSubscription({ requestId } as any, {
    onData: (deletedQuoteId: number) => {
      updateQuotes(null, { id: deletedQuoteId });
    },
  });

  return {
    quotes,
    isLoading,
    error,
  };
};
